import Cookies from 'js-cookie';
import { readItem, writeItem, removeItem } from './sessionStore';

/*
 * SSO detection that also works when third party cookies are blocked.
 *
 * `session.get()` (cross site XHR) and `token.getWithoutPrompt()` (hidden iframe) can
 * only see the Okta session cookie while the browser allows third party cookies. When
 * they are blocked both report "no session" even for a user that is already signed in
 * on the Okta domain, so we end up asking for the password again - typically when the
 * user lands here from a link on another Syngenta property.
 *
 * The fallback is a top level redirect to /authorize with `prompt=none`: that is a
 * first party navigation on the Okta domain, so the session cookie is always sent.
 * Okta either brings us back with a code (user is signed in) or with
 * `error=login_required` (no session), and in that case we go back to the sign in
 * widget. Because it costs one extra round trip we only do it once per browser tab,
 * and only after the silent check has failed.
 */

// Our own marker, one browser tab wide.
const SSO_CHECKED_KEY = 'ga-sso-checked';
const SSO_RETURN_PATH_KEY = 'ga-sso-return-path';

// Written by okta-auth-js when a redirect flow starts, read back on the callback.
const OKTA_REDIRECT_PARAMS_KEY = 'okta-oauth-redirect-params';
const OKTA_PKCE_KEY = 'okta-pkce-storage';
const OKTA_STATE_COOKIE = 'okta-oauth-state';
const OKTA_NONCE_COOKIE = 'okta-oauth-nonce';

// Errors Okta returns when `prompt=none` cannot be satisfied without user interaction.
const SILENT_AUTH_ERRORS = [
  'login_required',
  'interaction_required',
  'consent_required',
];

/**
 * True once this tab knows whether Okta has an SSO session for us, either because we
 * already asked with a redirect or because the user signed out on purpose.
 */
const hasCheckedSsoSession = () => readItem(SSO_CHECKED_KEY) === 'true';

/**
 * Stops the redirect check for the rest of the tab. Used on sign out, where we know
 * there is no session left to find.
 */
const markSsoSessionChecked = () => writeItem(SSO_CHECKED_KEY, 'true');

/**
 * Remembers where to come back to when Okta answers that there is no session.
 */
const beginSsoRedirectCheck = () => {
  markSsoSessionChecked();
  writeItem(
    SSO_RETURN_PATH_KEY,
    `${window.location.pathname}${window.location.search}`
  );
};

const cancelSsoRedirectCheck = () => removeItem(SSO_RETURN_PATH_KEY);

const takeSsoReturnPath = () => {
  const returnPath = readItem(SSO_RETURN_PATH_KEY);
  removeItem(SSO_RETURN_PATH_KEY);
  return returnPath;
};

/**
 * Reads the OAuth error of a `prompt=none` redirect that Okta could not fulfill.
 * Returns null for a regular callback, including a real error worth showing.
 */
const getSilentAuthError = (search) => {
  const error = new URLSearchParams(search || '').get('error');
  return SILENT_AUTH_ERRORS.indexOf(error) !== -1 ? error : null;
};

/**
 * Drops the redirect state okta-auth-js left behind, so the aborted `prompt=none`
 * flow does not clash with the next sign in.
 */
const clearOktaRedirectState = () => {
  removeItem(OKTA_REDIRECT_PARAMS_KEY);
  removeItem(OKTA_PKCE_KEY);
  try {
    window.localStorage.removeItem(OKTA_PKCE_KEY);
  } catch (e) {
    // nothing to clean up
  }
  [OKTA_REDIRECT_PARAMS_KEY, OKTA_STATE_COOKIE, OKTA_NONCE_COOKIE].forEach(
    (name) => Cookies.remove(name, { path: '/' })
  );
};

export {
  hasCheckedSsoSession,
  markSsoSessionChecked,
  beginSsoRedirectCheck,
  cancelSsoRedirectCheck,
  takeSsoReturnPath,
  getSilentAuthError,
  clearOktaRedirectState,
};
